import { pathPoints } from './svgPath'
import type { RunwayLayout, RunwayLayoutRunway, RunwayPoint } from './types'

export const DEFAULT_LABEL_DISTANCE = 22
export const RUNWAY_DRAWING_PADDING = 8
export const ARROW_STACK_STEP = 20
export const ALONG_RUNWAY_ARROW_GAP = 24

export type ArrowDirection = 'down' | 'up' | 'right' | 'left'

export const ARROW_TRANSFORMS: Record<ArrowDirection, string> = {
    down: 'translate(-12, -30)',
    up: 'rotate(180 0 0) translate(-12, -20)',
    right: 'rotate(-90 0 0) translate(-5, -25)',
    left: 'rotate(90 0 0) translate(-17, -23)',
}

interface Box {
    minX: number
    minY: number
    maxX: number
    maxY: number
}

const ARROW_BOXES: Record<ArrowDirection, Box> = {
    down: { minX: -12, maxX: 12, minY: -30, maxY: -18 },
    up: { minX: -12, maxX: 12, minY: 8, maxY: 20 },
    right: { minX: -25, maxX: -13, minY: -19, maxY: 5 },
    left: { minX: 11, maxX: 23, minY: -17, maxY: 7 },
}

const TEXT_BOX: Box = { minX: -16, maxX: 16, minY: -13, maxY: 3 }
const TEXT_BASELINE_OFFSET = 5
const ARROW_HALF_WIDTH = 12
const ARROW_HALF_HEIGHT = 6
const ARROW_RADIUS = Math.hypot(ARROW_HALF_WIDTH, ARROW_HALF_HEIGHT)

export interface RunwayCodePlacement {
    x: number
    y: number
    direction: ArrowDirection
    transform: string
    stack: RunwayPoint
    arrow: RunwayPoint
}

export interface RunwayDrawing {
    width: number
    height: number
    offset: RunwayPoint
    codes: [RunwayCodePlacement, RunwayCodePlacement][]
}

export const runwayLength = ({ from, to }: RunwayLayoutRunway): number =>
    Math.hypot(to.x - from.x, to.y - from.y)

export const runwayAngle = ({ from, to }: RunwayLayoutRunway): number =>
    Math.atan2(to.y - from.y, to.x - from.x)

const unitAlong = (runway: RunwayLayoutRunway): RunwayPoint => {
    const length = runwayLength(runway)
    return length === 0
        ? { x: 1, y: 0 }
        : { x: (runway.to.x - runway.from.x) / length, y: (runway.to.y - runway.from.y) / length }
}

const round = (value: number) => Math.round(value * 100) / 100

export const runwayBarCorners = (runway: RunwayLayoutRunway): RunwayPoint[] => {
    const along = unitAlong(runway)
    const half = runway.width / 2
    const normal = { x: -along.y * half, y: along.x * half }
    const { from, to } = runway
    return [
        { x: from.x + normal.x, y: from.y + normal.y },
        { x: to.x + normal.x, y: to.y + normal.y },
        { x: to.x - normal.x, y: to.y - normal.y },
        { x: from.x - normal.x, y: from.y - normal.y },
    ]
}

export const runwayBarPath = (runway: RunwayLayoutRunway): string =>
    `${runwayBarCorners(runway)
        .map((point, index) => `${index === 0 ? 'M' : 'L'}${round(point.x)} ${round(point.y)}`)
        .join(' ')} Z`

export const runwayShapePath = (runway: RunwayLayoutRunway): string =>
    runway.outline ?? runwayBarPath(runway)

const OUTWARD: Record<ArrowDirection, RunwayPoint> = {
    down: { x: 0, y: -1 },
    up: { x: 0, y: 1 },
    right: { x: -1, y: 0 },
    left: { x: 1, y: 0 },
}

const directionOf = (outward: RunwayPoint): ArrowDirection => {
    if (Math.abs(outward.y) >= Math.abs(outward.x)) {
        return outward.y < 0 ? 'down' : 'up'
    }
    return outward.x < 0 ? 'right' : 'left'
}

const toDegrees = (radians: number) => (radians * 180) / Math.PI

const alongRunwayPlacement = (
    point: RunwayPoint,
    outward: RunwayPoint,
    direction: ArrowDirection,
    distance: number,
): RunwayCodePlacement => {
    const x = Math.round(point.x + outward.x * distance)
    const y = Math.round(point.y + outward.y * distance + TEXT_BASELINE_OFFSET)
    const arrow = {
        x: round(point.x + outward.x * (distance + ALONG_RUNWAY_ARROW_GAP) - x),
        y: round(point.y + outward.y * (distance + ALONG_RUNWAY_ARROW_GAP) - y),
    }
    const rotation = round(toDegrees(Math.atan2(outward.x, -outward.y)))
    return {
        x,
        y,
        direction,
        transform: `translate(${arrow.x}, ${arrow.y}) rotate(${rotation}) translate(-${ARROW_HALF_WIDTH}, -${ARROW_HALF_HEIGHT})`,
        stack: {
            x: round(outward.x * ARROW_STACK_STEP),
            y: round(outward.y * ARROW_STACK_STEP),
        },
        arrow,
    }
}

export const runwayCodePlacement = (
    runway: RunwayLayoutRunway,
    end: 0 | 1,
    distance: number = DEFAULT_LABEL_DISTANCE,
    arrowsAlongRunway = false,
): RunwayCodePlacement => {
    const along = unitAlong(runway)
    const outward = end === 0 ? { x: -along.x, y: -along.y } : along
    const point = end === 0 ? runway.from : runway.to
    const direction = directionOf(outward)
    if (arrowsAlongRunway) {
        return alongRunwayPlacement(point, outward, direction, distance)
    }
    const step = OUTWARD[direction]
    const box = ARROW_BOXES[direction]
    return {
        x: Math.round(point.x + step.x * distance),
        y: Math.round(point.y + step.y * distance + TEXT_BASELINE_OFFSET),
        direction,
        transform: ARROW_TRANSFORMS[direction],
        stack: { x: step.x * ARROW_STACK_STEP || 0, y: step.y * ARROW_STACK_STEP || 0 },
        arrow: { x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2 },
    }
}

const emptyBox = (): Box => ({
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
})

const include = (box: Box, { x, y }: RunwayPoint) => {
    box.minX = Math.min(box.minX, x)
    box.minY = Math.min(box.minY, y)
    box.maxX = Math.max(box.maxX, x)
    box.maxY = Math.max(box.maxY, y)
}

const includeBox = (box: Box, part: Box, dx: number, dy: number) => {
    include(box, { x: part.minX + dx, y: part.minY + dy })
    include(box, { x: part.maxX + dx, y: part.maxY + dy })
}

const shapePoints = (runway: RunwayLayoutRunway): RunwayPoint[] => {
    if (runway.outline) {
        try {
            return pathPoints(runway.outline)
        } catch {
            return runwayBarCorners(runway)
        }
    }
    return runwayBarCorners(runway)
}

export const runwayLayoutBounds = (layout: RunwayLayout): Box => {
    const box = emptyBox()
    const distance = layout.labelDistance ?? DEFAULT_LABEL_DISTANCE
    const stacked = Math.max(layout.modes.length, 1)
    const arrowRadius = {
        minX: -ARROW_RADIUS,
        maxX: ARROW_RADIUS,
        minY: -ARROW_RADIUS,
        maxY: ARROW_RADIUS,
    }
    layout.runways.forEach(runway => {
        shapePoints(runway).forEach(point => include(box, point))
        ;([0, 1] as const).forEach(end => {
            const code = runwayCodePlacement(runway, end, distance, layout.arrowsAlongRunway)
            includeBox(box, TEXT_BOX, code.x, code.y)
            for (let index = 0; index < stacked; index += 1) {
                const stackX = code.x + code.stack.x * index
                const stackY = code.y + code.stack.y * index
                if (layout.arrowsAlongRunway) {
                    includeBox(box, arrowRadius, stackX + code.arrow.x, stackY + code.arrow.y)
                } else {
                    includeBox(box, ARROW_BOXES[code.direction], stackX, stackY)
                }
            }
        })
    })
    return Number.isFinite(box.minX) ? box : { minX: 0, minY: 0, maxX: 0, maxY: 0 }
}

export const runwayDrawing = (layout: RunwayLayout): RunwayDrawing => {
    const distance = layout.labelDistance ?? DEFAULT_LABEL_DISTANCE
    const bounds = runwayLayoutBounds(layout)
    const offset = {
        x: Math.round(RUNWAY_DRAWING_PADDING - bounds.minX),
        y: Math.round(RUNWAY_DRAWING_PADDING - bounds.minY),
    }
    const shifted = (code: RunwayCodePlacement): RunwayCodePlacement => ({
        ...code,
        x: code.x + offset.x,
        y: code.y + offset.y,
    })
    return {
        width: Math.ceil(bounds.maxX - bounds.minX + RUNWAY_DRAWING_PADDING * 2),
        height: Math.ceil(bounds.maxY - bounds.minY + RUNWAY_DRAWING_PADDING * 2),
        offset,
        codes: layout.runways.map(runway => [
            shifted(runwayCodePlacement(runway, 0, distance, layout.arrowsAlongRunway)),
            shifted(runwayCodePlacement(runway, 1, distance, layout.arrowsAlongRunway)),
        ]),
    }
}
